import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Tag } from "lucide-react";

export default function SuperAdminPricing() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});

  const refresh = async () => {
    const { data } = await api.get("/superadmin/service-pricing");
    setRows(data.items || []);
  };
  useEffect(() => { refresh().catch(() => toast.error("Couldn't load pricing")); }, []);

  const onEdit = (sk, val) => setEdits((e) => ({ ...e, [sk]: val }));

  const save = async (row) => {
    const v = parseFloat(edits[row.service_key]);
    if (isNaN(v) || v < 0) return toast.error("Enter a valid non-negative price");
    setSaving((s) => ({ ...s, [row.service_key]: true }));
    try {
      await api.put(`/superadmin/service-pricing/${row.service_key}`, { price_per_unit: v });
      toast.success(`${row.label} updated to ₹${v.toFixed(2)}`);
      setEdits((e) => { const { [row.service_key]: _, ...rest } = e; return rest; });
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving((s) => ({ ...s, [row.service_key]: false })); }
  };

  // Group by category for readability
  const byCat = rows.reduce((m, r) => ({ ...m, [r.category || "other"]: [...(m[r.category || "other"] || []), r] }), {});

  return (
    <div className="space-y-6 max-w-4xl" data-testid="superadmin-pricing-page">
      <div>
        <Link to="/superadmin/wallets" className="text-xs text-[hsl(var(--kb-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--kb-primary))]">
          <ArrowLeft size={12} /> Back to wallets
        </Link>
        <h1 className="font-display text-3xl md:text-4xl mt-1 flex items-center gap-2">
          <Tag size={22} /> Service Pricing
        </h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          These rates apply to every tenant wallet. Change a rate and it takes effect for the next usage event.
          Past charges are NOT retroactively adjusted.
        </p>
      </div>

      {Object.entries(byCat).map(([cat, items]) => (
        <section key={cat} className="bg-white border border-[hsl(var(--kb-border))]" data-testid={`pricing-cat-${cat}`}>
          <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] font-medium uppercase tracking-wider text-xs">
            {cat}
          </div>
          <ul className="divide-y divide-[hsl(var(--kb-border))]">
            {items.map((row) => {
              const isEditing = edits[row.service_key] !== undefined;
              return (
                <li key={row.service_key} className="p-5 flex items-center gap-4" data-testid={`pricing-row-${row.service_key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{row.label}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">
                      <span className="font-mono">{row.service_key}</span> · {row.unit}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[hsl(var(--kb-muted))]">₹</span>
                    <input
                      className="kb-input w-28 text-right"
                      type="number"
                      step="0.01"
                      min={0}
                      value={isEditing ? edits[row.service_key] : row.price_per_unit}
                      onChange={(e) => onEdit(row.service_key, e.target.value)}
                      data-testid={`pricing-input-${row.service_key}`}
                    />
                    <button
                      type="button"
                      disabled={!isEditing || saving[row.service_key]}
                      onClick={() => save(row)}
                      className="kb-btn-primary !py-2 !px-3 !text-xs disabled:opacity-40"
                      data-testid={`pricing-save-${row.service_key}`}
                    >
                      {saving[row.service_key] ? <Loader2 size={12} className="animate-spin" /> : "Save"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
